import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import {
    MentorDashboardStatsResponse,
    MentorSessionHistoryRequest,
    MentorSessionHistoryResponse,
    UpdateBookingStatusRequest,
    UpdateBookingStatusResponse,
    UpcomingSessionsResponse,
    MentorBookingsResponse,
    CompleteBookingResponse
} from '../../models/mentor-panel/mentor-panel-api-models';
import { ApiBaseResponse } from '../../models/api-base-model';

@Injectable({
    providedIn: 'root'
})
export class MentorPanelService {
    private apiUrl = environment.mentorApiUrl;

    constructor(private http: HttpClient) { }

    /**
     * Get mentor dashboard stats
     * @param mentorId Optional mentor ID
     */
    getDashboardStats(mentorId?: string): Observable<MentorDashboardStatsResponse> {
        let params = new HttpParams();
        if (mentorId) {
            params = params.set('mentor_id', mentorId);
        }
        return this.http.get<MentorDashboardStatsResponse>(`${this.apiUrl}/dashboard/stats`, { params });
    }

    /**
     * Get mentor session history with filters and pagination
     * @param request Filter and pagination parameters
     */
    getSessionHistory(request: MentorSessionHistoryRequest): Observable<MentorSessionHistoryResponse> {
        let params = new HttpParams();
        if (request.filter) params = params.set('filter', request.filter);
        if (request.page) params = params.set('page', request.page.toString());
        if (request.limit) params = params.set('limit', request.limit.toString());
        if (request.mentor_id) params = params.set('mentor_id', request.mentor_id);

        return this.http.get<MentorSessionHistoryResponse>(`${this.apiUrl}/sessions/history`, { params });
    }

    /**
     * Accept or reject a booking
     * @param bookingId The ID of the booking to update
     * @param request Action (true for accept, false for reject) and optional mentor ID
     */
    updateBookingStatus(bookingId: string, request: UpdateBookingStatusRequest): Observable<UpdateBookingStatusResponse> {
        return this.http.post<UpdateBookingStatusResponse>(`${this.apiUrl}/${bookingId}/update-status`, request);
    }

    /**
     * Get list of upcoming sessions for the mentor
     * @param mentorId Optional mentor ID
     */
    getUpcomingSessions(mentorId?: string): Observable<UpcomingSessionsResponse> {
        let params = new HttpParams();
        if (mentorId) {
            params = params.set('mentor_id', mentorId);
        }
        return this.http.get<UpcomingSessionsResponse>(`${this.apiUrl}/mentors/upcoming-sessions`, { params });
    }

    /**
     * Get general mentor bookings for the dashboard (pending + accepted)
     * @param mentorId Optional mentor ID
     */
    getMentorBookings(mentorId?: string): Observable<MentorBookingsResponse> {
        let params = new HttpParams();
        if (mentorId) {
            params = params.set('mentor_id', mentorId);
        }
        return this.http.get<MentorBookingsResponse>(`${this.apiUrl}/`, { params });
    }

    /**
     * Mark a booking as completed
     * @param bookingId The ID of the booking to mark as done
     */
    markBookingAsDone(bookingId: string): Observable<CompleteBookingResponse> {
        return this.http.post<CompleteBookingResponse>(`${this.apiUrl}/bookings/${bookingId}/done`, {});
    }
}
